import { clearLocalStorage } from 'api/api.utils'
import { queryClient } from 'helpers/queryClient'
import { isApiExperimentEnabled, isHoldbackApiExperimentEnabled } from 'utils/experiments/experiments'
import getLogger from 'utils/log/log'
import sentry from 'utils/sentry/sentry'

const log = getLogger('apiClient')

const baseUrlDefault = process.env.REACT_APP_API_URL ?? `http://localhost:8000/api`
const baseUrlExperiment = process.env.REACT_APP_API_URL_EXPERIMENT ?? baseUrlDefault
const baseUrlHoldback = process.env.REACT_APP_API_URL_HOLDBACK ?? baseUrlDefault

// Get base url based on the experiment setting.
// This allows us to switch to the new API domain for testing.
// See: https://app-eu.wrike.com/open.htm?id=4334574467
function getBaseUrl(): string {
  // Check experiments storage first (works for logged out users too)
  if (isHoldbackApiExperimentEnabled()) {
    return baseUrlHoldback
  } else if (isApiExperimentEnabled()) {
    return baseUrlExperiment
  }
  return baseUrlDefault
}

export function getUserFromLocalStorage(): User | null {
  const userItem = localStorage.getItem('user')
  const user = userItem ? JSON.parse(userItem) : null

  return user
}
function getImpersonatingUserFromLocalStorage(): User | null {
  const userItem = localStorage.getItem('impersonatingUser')
  const user = userItem ? JSON.parse(userItem) : null
  return user
}

function formatParams(params: Params, addBracketsToArrayKeys = true): [string, string][] {
  return Object.entries(params).reduce<[string, string][]>((acc, [key, value]) => {
    if (value !== undefined) {
      if (Array.isArray(value)) {
        const arrayParams = value.map((arrayValue) => {
          // `addBracketsToArrayKeys` is used to add brackets to array query params (`?key[]=val1&key[]=val2`).
          const qpKey = addBracketsToArrayKeys ? `${key}[]` : key
          return [qpKey, arrayValue.toString()] as [string, string]
        })
        return [...acc, ...arrayParams]
      }
      return [...acc, [key, value.toString()]]
    }
    return acc
  }, [])
}

type ParamType = string | number | boolean
type Param = ParamType | ParamType[] | undefined
type Params = Record<string, Param>

class ApiError extends Error {
  status: number
  url: string
  method: string
  body: unknown

  constructor(message: string, opts: { status: number; url: string; method: string; body: unknown }) {
    super(message)
    this.status = opts.status
    this.url = opts.url
    this.method = opts.method
    this.body = opts.body
  }
}

function getErrorCode(err: unknown): string | undefined {
  const body = (err as { body?: unknown })?.body

  if (body && typeof body === 'object' && 'code' in body && typeof body.code === 'string') {
    return body.code
  }

  return undefined
}

function getErrorDetail(err: unknown): string | undefined {
  const body = (err as { body?: unknown })?.body

  if (body && typeof body === 'object' && 'detail' in body && typeof body.detail === 'string') {
    return body.detail
  }

  return undefined
}

type ReportApiErrorParams = {
  scope: 'apiClient' | 'spreadsheetApiClient'
  endpoint: string
  url: string
  err: unknown
}

export function reportApiError({ scope, endpoint, url, err }: ReportApiErrorParams): void {
  const status = (err as { status?: number })?.status

  if (!status || status < 400) {
    return
  }

  // A 401 is expected whenever a token expires and is already handled by the forced logout.
  // Reporting it would flood Sentry with one message per in-flight request.
  if (status === 401) {
    return
  }

  const method = (err as { method?: string })?.method
  const code = getErrorCode(err)
  const detail = getErrorDetail(err)
  const body = (err as { body?: unknown })?.body

  sentry.captureApiMessage({
    scope,
    endpoint,
    url,
    method,
    status,
    code,
    detail,
    body,
  })
}

type Config = RequestInit & {
  method?: `GET` | `POST` | `PATCH` | `PUT` | `DELETE`
  params?: Params
  data?: Record<string, unknown>
  ignoreTrailingBackslash?: boolean
  addBracketsToArrayParamKeys?: boolean
}

/**
 * apiClient – unified HTTP wrapper returning parsed JSON/text or null.
 *
 * Error & special-case behavior summary:
 *
 * Scenario                 | Behavior
 * -------------------------|-----------------------------------------------
 * Network failure          | (throws native fetch error; not ApiError)
 * HTTP error (e.g. 500)    | throws ApiError("Request failed with...")
 * Unauthorized 401         | throws ApiError; ends the session if the request carried a token
 *
 * Notes:
 * - 401 throws like any other >= 400 status. The redirect that ending the session triggers is
 *   asynchronous, so callers keep rendering for a moment afterwards; returning the error body as
 *   `TResponseData` let them treat it as a successful payload during that window.
 * - Only an authenticated request's 401 ends the session. A 401 on an unauthenticated one (login,
 *   SSO, password reset) is a rejected attempt, and its caller stays mounted to report it.
 * - 401 is not reported to Sentry (see `reportApiError`) and is not retried (see `shouldRetryQuery`).
 * - Network-level failures (DNS, refusal, abort, etc.) throw native fetch errors and bubble up to caller.
 * - Overrides can contain any parameters (method, body, etc.). This is used for PUT and DELETE requests,
 *   as well as when body contains non-JSON data (e.g. FormData).
 */
export async function apiClient<TResponseData>(
  endpoint: string,
  { params, data, headers, addBracketsToArrayParamKeys, ...overrides }: Config = {}
): Promise<TResponseData> {
  const user = getUserFromLocalStorage()
  const impersonatingUser = getImpersonatingUserFromLocalStorage()
  const language = localStorage.getItem('language') ?? 'en'

  const defaults = {
    method: data ? 'POST' : 'GET',
    body: data ? JSON.stringify(data) : undefined,
    headers: {
      ...(user ? { Authorization: `Token ${user.token}` } : undefined),
      ...(language ? { 'Accept-Language': language } : undefined),
      ...(data ? { 'Content-Type': 'application/json' } : undefined),
      ...(impersonatingUser
        ? { 'Impersonating-User': `${impersonatingUser.id},${impersonatingUser.email}` }
        : undefined),
      ...headers,
    },
  }

  const baseUrl = getBaseUrl()
  let url = overrides?.ignoreTrailingBackslash ? `${baseUrl}/${endpoint}` : `${baseUrl}/${endpoint}/`
  if (params) {
    url += `?${new URLSearchParams(
      formatParams(params, addBracketsToArrayParamKeys === undefined || addBracketsToArrayParamKeys)
    )}`
  }

  try {
    const requestInit = { ...defaults, ...overrides }
    const response = await fetch(url, requestInit)

    // Precedes parsing so that a 401 whose body is unparseable still ends the session rather than
    // leaving the rejected token in place.
    const isSessionExpired = response.status === 401 && Boolean(user)
    if (isSessionExpired) {
      clearLocalStorage()
      sessionStorage.clear()
      queryClient.clear()
      window.location.replace(`/`)
    }

    let parsed
    try {
      parsed = await parseResponse(response)
    } catch (parseErr: any) {
      throw new ApiError(`Failed to parse response: ${parseErr.message || parseErr}`, {
        status: response.status,
        url,
        method: requestInit.method,
        body: null,
      })
    }

    // Throw error for client errors (4xx) and server errors (5xx)
    // But allow redirects (3xx) and other non-error statuses through
    if (response.status >= 400) {
      throw new ApiError(`Request failed with status ${response.status}`, {
        status: response.status,
        url,
        method: requestInit.method,
        body: parsed,
      })
    }

    return parsed
  } catch (err: any) {
    log.warn('apiClient warning:', { url, err })
    reportApiError({ scope: 'apiClient', endpoint, url, err })
    throw err
  }
}

// Own ApiClient for SettlementsExcelExport
export type SpreadsheetResult = { result?: ArrayBuffer; filename?: string }

/**
 * spreadsheetApiClient – unified HTTP wrapper for spreadsheet downloads.
 *
 * Error & special-case behavior summary:
 *
 * Scenario                 | Behavior
 * -------------------------|-----------------------------------------------
 * Network failure          | (throws native fetch error; not ApiError)
 * HTTP error (e.g. 500)    | throws ApiError("Request failed with...")
 * Unauthorized 401         | returns SpreadsheetResult after logout (no error)
 *
 * Notes:
 * - 401 forces logout side-effects then returns SpreadsheetResult with result/filename (no ApiError).
 * - Network-level failures (DNS, refusal, abort, etc.) throw native fetch errors and bubble up to caller.
 */
export async function spreadsheetApiClient(
  endpoint: string,
  { params, data, headers, addBracketsToArrayParamKeys, ignoreTrailingBackslash, ...overrides }: Config = {}
): Promise<SpreadsheetResult | null> {
  const user = getUserFromLocalStorage()
  const impersonatingUser = getImpersonatingUserFromLocalStorage()
  const language = localStorage.getItem('language') ?? 'en'

  const method = overrides.method ?? 'GET'
  const defaults: RequestInit = {
    method,
    body: data ? JSON.stringify(data) : undefined,
    headers: {
      ...(user ? { Authorization: `Token ${user.token}` } : undefined),
      ...(language ? { 'Accept-Language': language } : undefined),
      ...(data ? { 'Content-Type': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' } : undefined),
      ...(impersonatingUser
        ? { 'Impersonating-User': `${impersonatingUser.id},${impersonatingUser.email}` }
        : undefined),
      ...headers,
    },
  }

  const baseUrl = getBaseUrl()
  let url = ignoreTrailingBackslash ? `${baseUrl}/${endpoint}` : `${baseUrl}/${endpoint}/`
  if (params) {
    url += `?${new URLSearchParams(
      formatParams(params, addBracketsToArrayParamKeys === undefined || addBracketsToArrayParamKeys)
    )}`
  }

  try {
    const requestInit = { ...defaults, ...overrides }
    const response = await fetch(url, requestInit)

    const contentType = response.headers.get('content-type') || ''
    const contentDisposition = response.headers.get('content-disposition')
    const filename = contentDisposition
      ?.split(';')
      .find((n: string) => n.includes('filename='))
      ?.replace('filename=', '')
      .trim()

    let result: ArrayBuffer | undefined

    // Parse the response body for spreadsheet content
    if (contentType.includes('application/vnd.openxmlformats-officedocument.spreadsheetml.sheet')) {
      result = await response.arrayBuffer()
    }

    if (response.status === 401) {
      clearLocalStorage()
      sessionStorage.clear()
      queryClient.clear()
      window.location.replace('/')
      return {
        result,
        filename,
      }
    }

    // Throw error for client errors (4xx) and server errors (5xx)
    // But allow redirects (3xx) and other non-error statuses through
    if (response.status >= 400) {
      throw new ApiError(`Request failed with status ${response.status}`, {
        status: response.status,
        url,
        method,
        body: result,
      })
    }

    return {
      result,
      filename,
    }
  } catch (err: any) {
    log.warn('spreadsheetApiClient warning:', { url, err })
    reportApiError({ scope: 'spreadsheetApiClient', endpoint, url, err })
    throw err
  }
}

async function parseResponse(response: Response): Promise<any> {
  const contentType = response.headers.get('content-type') || ''
  if (contentType.includes('application/json')) {
    return response.json()
  } else if (contentType.includes('text/')) {
    return response.text()
  } else {
    return null
  }
}
